/* eslint-disable @atlaskit/design-system/no-html-anchor */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, jsx } from '@compiled/react';
import type { ComponentProps, ReactNode } from 'react';
import { useState } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { ConfluenceLogo } from '@atlaskit/logo';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { ANALYTICS_CHANNEL } from '../analytics/AnalyticsListener';

const FOOTER_CONTENT_HEIGHT = 52;
const FOOTER_BORDER_PX = 2;
export const FOOTER_HEIGHT: number = FOOTER_CONTENT_HEIGHT + FOOTER_BORDER_PX;

const containerStyles = css({
	font: token('font.body.small'),
	flex: `0 0 ${FOOTER_CONTENT_HEIGHT}px`,
	height: FOOTER_CONTENT_HEIGHT,
	color: token('color.text.subtle'),
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	borderTop: `${FOOTER_BORDER_PX}px solid ${token('color.border')}`,
});

const contentWrapperStyles = css({
	height: 16,
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'flex-end',
});

const logoWrapperSyles = css({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	marginTop: '0px',
	marginRight: '0px',
	marginBottom: '0px',
	marginLeft: token('space.100'),
});

const linkStyles = css({
	color: 'inherit',
	outline: 'none',
	boxShadow: 'none',
	textDecoration: 'none',

	'&:focus, &:active, &:hover': {
		color: 'inherit',
		outline: 'none',
		boxShadow: 'none',
		textDecoration: 'none',
	},
});

export type FooterProps = Readonly<{
	label: ReactNode;
	linkTooltip: ReactNode;
	url: string | null;
}>;

export const Footer = ({ url, linkTooltip, label }: FooterProps): JSX.Element => (
	<div css={containerStyles} data-testid="footer-container">
		{url ? (
			<ContentAction tooltipText={linkTooltip} href={url}>
				{({ isHovered }) => <Content isHovered={isHovered} label={label} />}
			</ContentAction>
		) : (
			<Content label={label} />
		)}
	</div>
);

type ContentProps = Readonly<{
	isHovered?: boolean;
	label: ReactNode;
}>;

const Content = ({ isHovered, label }: ContentProps) => (
	<div css={contentWrapperStyles}>
		{label}
		<div css={logoWrapperSyles}>
			<ConfluenceLogo appearance={isHovered ? 'brand' : 'neutral'} size="xxsmall" />
		</div>
	</div>
);

type ContentActionProps = {
	children: ({ isHovered }: { isHovered: boolean }) => ReactNode;
	href: string;
	tooltipText: ComponentProps<typeof Tooltip>['content'];
};

const ContentAction = ({ children, tooltipText, href }: ContentActionProps) => {
	const [isHovered, setIsHovered] = useState<boolean>(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleClick = () => {
		createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'confluenceFooter',
			source: 'embeddedConfluenceViewPageScreen',
			eventType: 'ui',
		}).fire(ANALYTICS_CHANNEL);
	};

	return (
		<Tooltip content={tooltipText}>
			{/* eslint-disable-next-line @atlaskit/design-system/no-html-anchor */}
			<a
				css={linkStyles}
				href={href}
				target="_blank"
				onMouseEnter={() => {
					setIsHovered(true);
				}}
				onMouseLeave={() => {
					setIsHovered(false);
				}}
				onFocus={() => {
					setIsHovered(true);
				}}
				onBlur={() => {
					setIsHovered(false);
				}}
				onClick={handleClick}
				data-testid="link"
			>
				{children({ isHovered })}
			</a>
		</Tooltip>
	);
};
